import React, { useState, useRef } from 'react';
import { Box, Grid, Typography, IconButton, Button, ToggleButton, ToggleButtonGroup, Paper, Slider, FormControlLabel, Switch } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { ChromePicker } from 'react-color';

interface FarmAvatarCreatorProps {
  onAvatarSelect: (avatar: { type: 'shape' | 'image'; data: string }) => void;
  currentAvatar?: { type: 'shape' | 'image'; data: string };
}

const FarmAvatarCreator: React.FC<FarmAvatarCreatorProps> = ({ onAvatarSelect, currentAvatar }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedShape, setSelectedShape] = useState<string>(currentAvatar?.type === 'shape' ? currentAvatar.data.split(':')[0] : 'mountain');
  const [selectedColor, setSelectedColor] = useState<string>('#22c55e');
  const [gradientAngle, setGradientAngle] = useState<number>(45);
  const [useGradient, setUseGradient] = useState<boolean>(false);
  const [secondaryColor, setSecondaryColor] = useState<string>('#16a34a');
  const [showColorPicker, setShowColorPicker] = useState<{ primary: boolean; secondary: boolean }>({ primary: false, secondary: false });
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageOverlay, setImageOverlay] = useState<boolean>(false);
  const [overlayOpacity, setOverlayOpacity] = useState<number>(0.3);

  // Adventure-inspired shapes
  const shapes = [
    { id: 'mountain', name: 'Mountain', path: 'M 10 90 L 30 40 L 45 55 L 60 30 L 75 45 L 90 90 Z' },
    { id: 'compass', name: 'Compass', path: 'M 50 10 L 60 40 L 90 50 L 60 60 L 50 90 L 40 60 L 10 50 L 40 40 Z M 50 45 a 5 5 0 1 0 0 10 a 5 5 0 1 0 0 -10' },
    { id: 'tree', name: 'Tree', path: 'M 50 10 Q 30 30 35 40 Q 20 50 30 60 Q 15 70 40 80 L 40 90 L 60 90 L 60 80 Q 85 70 70 60 Q 80 50 65 40 Q 70 30 50 10' },
    { id: 'tent', name: 'Tent', path: 'M 50 20 L 20 70 L 30 70 L 30 90 L 70 90 L 70 70 L 80 70 Z M 40 70 L 60 70 L 50 40 Z' },
    { id: 'sun', name: 'Sun', path: 'M 50 30 a 20 20 0 1 0 0 40 a 20 20 0 1 0 0 -40 M 50 10 L 50 20 M 50 80 L 50 90 M 20 50 L 30 50 M 70 50 L 80 50 M 25 25 L 32 32 M 68 68 L 75 75 M 75 25 L 68 32 M 32 68 L 25 75' },
    { id: 'leaf', name: 'Leaf', path: 'M 50 90 Q 20 60 20 30 Q 20 10 40 10 Q 50 10 50 20 Q 50 10 60 10 Q 80 10 80 30 Q 80 60 50 90 M 50 20 L 50 80 M 35 40 L 50 50 M 65 40 L 50 50' },
    { id: 'adventure', name: 'Adventure', path: 'M 50 15 L 35 35 L 15 35 L 28 50 L 20 70 L 50 55 L 80 70 L 72 50 L 85 35 L 65 35 Z' },
    { id: 'map', name: 'Map', path: 'M 10 20 L 35 15 L 65 25 L 90 20 L 90 80 L 65 75 L 35 85 L 10 80 Z M 35 15 L 35 85 M 65 25 L 65 75' },
  ];

  const handleShapeSelect = (_: React.MouseEvent<HTMLElement>, shape: string | null) => {
    if (shape) {
      setSelectedShape(shape);
      setUploadedImage(null);
      updateAvatar('shape', shape, selectedColor, useGradient, secondaryColor, gradientAngle);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setUploadedImage(result);
        // Store image with overlay settings
        const imageData = imageOverlay 
          ? `${result}|overlay:${useGradient ? 'gradient' : 'solid'}:${selectedColor}:${secondaryColor}:${gradientAngle}:${overlayOpacity}`
          : result;
        onAvatarSelect({ type: 'image', data: imageData });
      };
      reader.readAsDataURL(file);
    }
  };

  const updateAvatar = (type: 'shape' | 'image', shape: string, color: string, gradient: boolean, secColor: string, angle: number) => {
    if (type === 'shape') {
      const data = gradient 
        ? `${shape}:gradient:${color}:${secColor}:${angle}`
        : `${shape}:solid:${color}`;
      onAvatarSelect({ type: 'shape', data });
    } else if (uploadedImage) {
      const imageData = imageOverlay 
        ? `${uploadedImage}|overlay:${gradient ? 'gradient' : 'solid'}:${color}:${secColor}:${angle}:${overlayOpacity}`
        : uploadedImage;
      onAvatarSelect({ type: 'image', data: imageData });
    }
  };

  const handleColorChange = (color: any, isPrimary: boolean) => {
    if (isPrimary) {
      setSelectedColor(color.hex);
      updateAvatar(uploadedImage ? 'image' : 'shape', selectedShape, color.hex, useGradient, secondaryColor, gradientAngle);
    } else {
      setSecondaryColor(color.hex);
      updateAvatar(uploadedImage ? 'image' : 'shape', selectedShape, selectedColor, useGradient, color.hex, gradientAngle);
    }
  };

  const renderAvatar = () => {
    if (uploadedImage) {
      return (
        <Box position="relative" width={120} height={120}>
          <img 
            src={uploadedImage} 
            alt="Uploaded" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              borderRadius: '8px'
            }} 
          />
          {imageOverlay && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius="8px"
              sx={{
                background: useGradient 
                  ? `linear-gradient(${gradientAngle}deg, ${selectedColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')}, ${secondaryColor}${Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')})`
                  : selectedColor + Math.round(overlayOpacity * 255).toString(16).padStart(2, '0')
              }}
            />
          )}
        </Box>
      );
    }

    const shapeData = shapes.find(s => s.id === selectedShape);
    if (!shapeData) return null;

    return (
      <svg width="120" height="120" viewBox="0 0 100 100">
        {useGradient && (
          <defs>
            <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%" gradientTransform={`rotate(${gradientAngle} 50 50)`}>
              <stop offset="0%" stopColor={selectedColor} />
              <stop offset="100%" stopColor={secondaryColor} />
            </linearGradient>
          </defs>
        )}
        <path 
          d={shapeData.path} 
          fill={useGradient ? 'url(#avatarGradient)' : selectedColor}
          stroke={useGradient ? 'none' : selectedColor}
          strokeWidth="1"
        />
      </svg>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Your Farm Avatar
      </Typography>

      {/* Current Avatar Preview */}
      <Box display="flex" justifyContent="center" mb={3}>
        <Paper sx={{ p: 3, bgcolor: 'grey.100' }}>
          {renderAvatar()}
        </Paper>
      </Box>

      {/* Shape Selection */}
      {!uploadedImage && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Choose an Adventure Shape
          </Typography>
          <ToggleButtonGroup
            value={selectedShape}
            exclusive
            onChange={handleShapeSelect}
            aria-label="shape selection"
            sx={{ mb: 3, flexWrap: 'wrap' }}
          >
            {shapes.map(shape => (
              <ToggleButton key={shape.id} value={shape.id} sx={{ p: 1, m: 0.5 }}>
                <Box textAlign="center">
                  <svg width="40" height="40" viewBox="0 0 100 100">
                    <path d={shape.path} fill={selectedColor} stroke={selectedColor} strokeWidth="1" />
                  </svg>
                  <Typography variant="caption" display="block">{shape.name}</Typography>
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </>
      )}

      {/* Color Options */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            Color Style
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={useGradient}
                onChange={(e) => {
                  setUseGradient(e.target.checked);
                  updateAvatar(uploadedImage ? 'image' : 'shape', selectedShape, selectedColor, e.target.checked, secondaryColor, gradientAngle);
                }}
              />
            }
            label="Use Gradient"
          />
        </Box>

        {/* Color Pickers */}
        <Grid container spacing={2}>
          <Grid item xs={useGradient ? 6 : 12}>
            <Box position="relative">
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ColorLensIcon />}
                onClick={() => setShowColorPicker({ ...showColorPicker, primary: !showColorPicker.primary })}
                sx={{ 
                  bgcolor: selectedColor, 
                  color: 'white',
                  '&:hover': { bgcolor: selectedColor }
                }}
              >
                {useGradient ? 'Primary Color' : 'Color'}
              </Button>
              {showColorPicker.primary && (
                <Box position="absolute" zIndex={2} mt={1}>
                  <Box position="fixed" top={0} right={0} bottom={0} left={0} onClick={() => setShowColorPicker({ ...showColorPicker, primary: false })} />
                  <ChromePicker 
                    color={selectedColor}
                    onChange={(color) => handleColorChange(color, true)}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          
          {useGradient && (
            <Grid item xs={6}>
              <Box position="relative">
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ColorLensIcon />}
                  onClick={() => setShowColorPicker({ ...showColorPicker, secondary: !showColorPicker.secondary })}
                  sx={{ 
                    bgcolor: secondaryColor, 
                    color: 'white',
                    '&:hover': { bgcolor: secondaryColor }
                  }}
                >
                  Secondary Color
                </Button>
                {showColorPicker.secondary && (
                  <Box position="absolute" zIndex={2} mt={1}>
                    <Box position="fixed" top={0} right={0} bottom={0} left={0} onClick={() => setShowColorPicker({ ...showColorPicker, secondary: false })} />
                    <ChromePicker 
                      color={secondaryColor}
                      onChange={(color) => handleColorChange(color, false)}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        {useGradient && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Gradient Angle: {gradientAngle}°
            </Typography>
            <Slider
              value={gradientAngle}
              onChange={(_, value) => {
                setGradientAngle(value as number);
                updateAvatar(uploadedImage ? 'image' : 'shape', selectedShape, selectedColor, true, secondaryColor, value as number);
              }}
              min={0}
              max={360}
            />
          </Box>
        )}
      </Box>

      {/* Upload Option */}
      <Box sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Or Upload Your Logo
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={handleImageUpload}
        />
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
          fullWidth
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadedImage ? 'Change Image' : 'Upload Image'}
        </Button>
        
        {uploadedImage && (
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={imageOverlay}
                  onChange={(e) => {
                    setImageOverlay(e.target.checked);
                    updateAvatar('image', selectedShape, selectedColor, useGradient, secondaryColor, gradientAngle);
                  }}
                />
              }
              label="Apply color overlay"
            />
            
            {imageOverlay && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Overlay Opacity: {Math.round(overlayOpacity * 100)}%
                </Typography>
                <Slider
                  value={overlayOpacity}
                  onChange={(_, value) => {
                    setOverlayOpacity(value as number);
                    updateAvatar('image', selectedShape, selectedColor, useGradient, secondaryColor, gradientAngle);
                  }}
                  min={0}
                  max={1}
                  step={0.1}
                />
              </Box>
            )}
            
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setUploadedImage(null);
                setImageOverlay(false);
                updateAvatar('shape', selectedShape, selectedColor, useGradient, secondaryColor, gradientAngle);
              }}
              sx={{ mt: 1 }}
            >
              Remove Image
            </Button>
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Image will be cropped to square. Supported formats: JPG, PNG, GIF
        </Typography>
      </Box>
    </Box>
  );
};

export default FarmAvatarCreator;